import type { DrawingDocument } from '../model';
import { isChemicalBond } from './documentSelectors.ts';
import { nextSequentialId, replaceTopology, roundCoordinate } from './drawingDocumentCore.ts';
import { implicitHydrogens } from '../chemistry/chemicalValidation.ts';

/** RDKit's and the thumbnails' bond length, in drawing units, when the drawing has no bond to measure. */
const DEFAULT_BOND_LENGTH = 30;

const METALS = (z: number) =>
  z === 3 ||
  z === 4 ||
  (z >= 11 && z <= 13) ||
  (z >= 19 && z <= 31) ||
  (z >= 37 && z <= 50) ||
  (z >= 55 && z <= 84) ||
  z >= 87;

/**
 * #777: each atom's implicit hydrogens drawn as atoms -- an H with a single bond -- in the widest gaps
 * between the atom's bonds, at the drawing's own bond length. `atomIds`: only these atoms (a
 * selection's molecules); all when absent. A drawn H is the same molecule (#951), so nothing about
 * its identity changes; the chemistry check re-reads stereo from the drawing.
 */
export function expandHydrogens(document: DrawingDocument, atomIds?: ReadonlySet<string>): DrawingDocument {
  const { structure } = document;
  const { coordinates } = document.depiction.coordinateSet;
  const counts = hydrogenCounts(document);
  const length = drawingBondLength(document);
  const nodes = [...structure.graph.nodes];
  const edges = [...structure.graph.edges];
  const atoms = { ...structure.atoms };
  const bonds = { ...structure.bonds };
  const formalCharges = { ...structure.formalCharges };
  const placed = { ...coordinates };
  let added = 0;
  for (const node of structure.graph.nodes) {
    const count = counts.get(node.id) ?? 0;
    const atom = structure.atoms[node.id];
    const at = coordinates[node.id];
    if (count === 0 || !atom || !at || atom.atomicNumber === 1 || (atomIds && !atomIds.has(node.id))) continue;
    const taken = structure.graph.edges.flatMap((edge) => {
      if (edge.source !== node.id && edge.target !== node.id) return [];
      const other = coordinates[edge.source === node.id ? edge.target : edge.source];
      return other ? [Math.atan2(other.y - at.y, other.x - at.x)] : [];
    });
    for (const angle of spreadAngles(taken, count)) {
      const id = nextSequentialId(
        nodes.map((each) => each.id),
        'n'
      );
      const edgeId = nextSequentialId(
        edges.map((each) => each.id),
        'e'
      );
      nodes.push({ id });
      edges.push({ id: edgeId, source: node.id, target: id });
      atoms[id] = { atomicNumber: 1 };
      bonds[edgeId] = { order: 1, type: 'covalent' };
      formalCharges[id] = 0;
      placed[id] = {
        x: roundCoordinate(at.x + Math.cos(angle) * length),
        y: roundCoordinate(at.y + Math.sin(angle) * length)
      };
      added++;
    }
  }
  if (added === 0) return document;
  return replaceTopology(
    document,
    { ...structure.graph, revision: structure.graph.revision + 1, nodes, edges },
    atoms,
    bonds,
    formalCharges,
    placed,
    document.depiction.atomLabels,
    document.depiction.bondLengthOverrides
  );
}

/**
 * #777: hydrogens drawn as atoms folded back into their neighbours, as the server folds them for
 * identity (#951) -- except one that says something of its own: an isotope, a charge or unpaired
 * electron, two bonds (bridging, agostic), a metal neighbour, or a drawn wedge or wavy bond, whose
 * stereo would otherwise be lost. A stereocentre that named it names its implicit hydrogen instead.
 */
export function collapseHydrogens(document: DrawingDocument, atomIds?: ReadonlySet<string>): DrawingDocument {
  const { structure } = document;
  const removed = foldableHydrogens(document, atomIds);
  if (removed.size === 0) return document;
  const nodes = structure.graph.nodes.filter((node) => !removed.has(node.id));
  const edges = structure.graph.edges.filter((edge) => !removed.has(edge.source) && !removed.has(edge.target));
  const keep = <T>(record: Readonly<Record<string, T>>, ids: ReadonlySet<string>) =>
    Object.fromEntries(Object.entries(record).filter(([id]) => ids.has(id)));
  const nodeIds = new Set(nodes.map((node) => node.id));
  const edgeIds = new Set(edges.map((edge) => edge.id));
  const collapsed = replaceTopology(
    document,
    { ...structure.graph, revision: structure.graph.revision + 1, nodes, edges },
    keep(structure.atoms, nodeIds),
    keep(structure.bonds, edgeIds),
    keep(structure.formalCharges, nodeIds),
    keep(document.depiction.coordinateSet.coordinates, nodeIds),
    keep(document.depiction.atomLabels, nodeIds),
    keep(document.depiction.bondLengthOverrides, edgeIds)
  );
  // A stereocentre that named a folded hydrogen names its implicit one; a double bond's reference
  // on one is dropped (the chemistry check re-reads it from the drawing).
  const tetrahedral = Object.fromEntries(
    Object.entries(collapsed.structure.stereochemistry.tetrahedral).map(([centre, stereo]) => [
      centre,
      {
        ...stereo,
        ligands: stereo.ligands.map((ligand) =>
          'atomId' in ligand && removed.has(ligand.atomId) ? { implicitHydrogen: true as const } : ligand
        ) as unknown as typeof stereo.ligands
      }
    ])
  );
  const doubleBonds = Object.fromEntries(
    Object.entries(collapsed.structure.stereochemistry.doubleBonds).filter(
      ([, stereo]) => !stereo.referenceAtomIds.some((id) => removed.has(id))
    )
  );
  return {
    ...collapsed,
    structure: { ...collapsed.structure, stereochemistry: { tetrahedral, doubleBonds } }
  };
}

/**
 * Each atom's implicit hydrogens by the chemistry check's own rule (its standard valence, moved by
 * its charge, less its bonds and unpaired electrons; none on a metal). A dative bond counts for its
 * acceptor only.
 */
function hydrogenCounts(document: DrawingDocument): ReadonlyMap<string, number> {
  const { structure } = document;
  const used = new Map<string, number>();
  for (const edge of structure.graph.edges) {
    const bond = structure.bonds[edge.id];
    if (!bond || !isChemicalBond(bond)) continue;
    const donor = bond.type === 'dative' ? bond.donorAtomId : undefined;
    for (const end of [edge.source, edge.target]) {
      if (end !== donor) used.set(end, (used.get(end) ?? 0) + (bond.order ?? 1));
    }
  }
  return new Map(
    structure.graph.nodes.map(({ id }) => {
      const atom = structure.atoms[id];
      if (!atom) return [id, 0];
      const unpaired = atom.radicalElectronCount ?? 0;
      return [
        id,
        implicitHydrogens(atom.atomicNumber, structure.formalCharges[id] ?? 0, (used.get(id) ?? 0) + unpaired)
      ];
    })
  );
}

/** #777: whether any hydrogen drawn as an atom among `atomIds` (all when absent) can be folded. */
export function hasFoldableHydrogens(document: DrawingDocument, atomIds?: ReadonlySet<string>): boolean {
  return foldableHydrogens(document, atomIds).size > 0;
}

/** The hydrogens {@link collapseHydrogens} folds: plain ones, none that says something of its own. */
function foldableHydrogens(document: DrawingDocument, atomIds?: ReadonlySet<string>): Set<string> {
  const { structure } = document;
  const styled = new Set(Object.keys(document.depiction.bondStyles));
  const removed = new Set<string>();
  for (const node of structure.graph.nodes) {
    const atom = structure.atoms[node.id];
    if (!atom || atom.atomicNumber !== 1 || (atomIds && !atomIds.has(node.id))) continue;
    if (atom.isotope !== undefined || atom.radicalElectronCount || (structure.formalCharges[node.id] ?? 0) !== 0)
      continue;
    const touching = structure.graph.edges.filter((edge) => edge.source === node.id || edge.target === node.id);
    const [edge] = touching;
    if (touching.length !== 1 || !edge || styled.has(edge.id)) continue;
    const bond = structure.bonds[edge.id];
    if (!bond || !isChemicalBond(bond) || bond.order !== 1 || bond.type !== 'covalent') continue;
    const neighbour = structure.atoms[edge.source === node.id ? edge.target : edge.source];
    if (!neighbour || neighbour.atomicNumber === 1 || METALS(neighbour.atomicNumber)) continue;
    removed.add(node.id);
  }
  return removed;
}

/** The drawing's typical bond length: the median of its chemical bonds. */
export function drawingBondLength(document: DrawingDocument): number {
  const { coordinates } = document.depiction.coordinateSet;
  const lengths = document.structure.graph.edges
    .filter((edge) => isChemicalBond(document.structure.bonds[edge.id]))
    .flatMap((edge) => {
      const a = coordinates[edge.source];
      const b = coordinates[edge.target];
      return a && b ? [Math.hypot(a.x - b.x, a.y - b.y)] : [];
    })
    .filter((length) => length > 0)
    .sort((a, b) => a - b);
  return lengths.length ? lengths[Math.floor(lengths.length / 2)]! : DEFAULT_BOND_LENGTH;
}

/**
 * `count` new directions around an atom with bonds at `taken` angles: each goes into the gap with the
 * most room left, gaps split evenly. A lone atom gets them spread round it.
 */
export function spreadAngles(taken: readonly number[], count: number): number[] {
  if (taken.length === 0) return Array.from({ length: count }, (_, index) => (2 * Math.PI * index) / count);
  const sorted = [...taken].sort((a, b) => a - b);
  const gaps = sorted.map((start, index) => {
    const next = index + 1 < sorted.length ? sorted[index + 1]! : sorted[0]! + 2 * Math.PI;
    return { start, size: next - start, count: 0 };
  });
  for (let index = 0; index < count; index++) {
    const roomiest = gaps.reduce((best, gap) =>
      gap.size / (gap.count + 1) > best.size / (best.count + 1) ? gap : best
    );
    roomiest.count++;
  }
  return gaps.flatMap((gap) =>
    Array.from({ length: gap.count }, (_, index) => gap.start + (gap.size * (index + 1)) / (gap.count + 1))
  );
}
